import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { HERO_LEAD } from "@/constants/testIds";
import { SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadForm = ({ variant = "hero" }) => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    business_name: "",
    mobile_phone: "",
    direct_phone: "",
    business_phone: "",
    funding_amount: "",
    funding_product: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.business_name || !form.mobile_phone) {
      toast.error("Please fill in name, email, business, and mobile number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("We've got your info — a broker will call within 24 hours (Mon–Fri).");
      setForm({
        first_name: "",
        last_name: "",
        email: "",
        business_name: "",
        mobile_phone: "",
        direct_phone: "",
        business_phone: "",
        funding_amount: "",
      });
    } catch (err) {
      toast.error("Submission failed. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, ease: [0.7, 0, 0.2, 1] }}
      onSubmit={submit}
      data-testid={HERO_LEAD.form}
      className={`relative border border-black/15 bg-[color:var(--bg-elev)] p-8 md:p-10 ${
        variant === "hero" ? "backdrop-blur-md" : ""
      }`}
    >
      <div className="absolute -top-3 left-8 bg-[color:var(--bg-elev)] px-3">
        <span className="overline">Fast quote</span>
      </div>

      <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-1">
        See what you qualify for.
      </h3>
      <p className="text-sm text-[color:var(--bone)]/60 mb-8">
        No credit pull. No minimum FICO. Just 4–6 months of business bank statements. A broker calls back within 24 hours during business hours (Mon–Fri).
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
        <div>
          <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
            First Name
          </label>
          <input
            data-testid={HERO_LEAD.name}
            className="editorial-input"
            value={form.first_name}
            onChange={change("first_name")}
            placeholder="Jane"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
            Last Name
          </label>
          <input
            className="editorial-input"
            value={form.last_name}
            onChange={change("last_name")}
            placeholder="Doe"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
            Email
          </label>
          <input
            data-testid={HERO_LEAD.email}
            type="email"
            className="editorial-input"
            value={form.email}
            onChange={change("email")}
            placeholder="jane@acme.com"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
            Business Name
          </label>
          <input
            className="editorial-input"
            value={form.business_name}
            onChange={change("business_name")}
            placeholder="Acme LLC"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
            Mobile Number
          </label>
          <input
            data-testid={HERO_LEAD.phone}
            className="editorial-input"
            value={form.mobile_phone}
            onChange={change("mobile_phone")}
            placeholder="(708) 555-0110"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
            Direct Number <span className="text-[color:var(--bone)]/35 normal-case tracking-normal">(optional)</span>
          </label>
          <input
            className="editorial-input"
            value={form.direct_phone}
            onChange={change("direct_phone")}
            placeholder="(708) 555-0111"
          />
        </div>
        <div className="md:col-span-2">
          <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
            Business Number <span className="text-[color:var(--bone)]/35 normal-case tracking-normal">(optional)</span>
          </label>
          <input
            className="editorial-input"
            value={form.business_phone}
            onChange={change("business_phone")}
            placeholder="(708) 555-0112"
          />
        </div>
        <div className="md:col-span-2">
          <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
            Funding Request?
          </label>
          <select
            data-testid={HERO_LEAD.amount}
            className="editorial-select mt-2"
            value={form.funding_amount}
            onChange={change("funding_amount")}
          >
            <option value="">Select an amount</option>
            <option>$5K</option>
            <option>$5K – $10K</option>
            <option>$10K – $15K</option>
            <option>$15K – $25K</option>
            <option>$25K – $50K</option>
            <option>$50K – $100K</option>
            <option>$100K – $250K</option>
            <option>$250K – $500K</option>
            <option>$500K – $1M</option>
            <option>$1M – $2.5M</option>
            <option>$2.5M – $5M</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
            Funding Product
          </label>
          <select
            className="editorial-select mt-2"
            value={form.funding_product}
            onChange={change("funding_product")}
          >
            <option value="">Select a product</option>
            {SERVICES.map((s) => (
              <option key={s.id} value={s.title}>{s.title}</option>
            ))}
            <option>Not sure — you decide</option>
          </select>
        </div>
      </div>

      <button
        data-testid={HERO_LEAD.submit}
        disabled={loading}
        type="submit"
        className="mt-8 w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[color:var(--gold)] text-black px-8 py-4 font-mono text-xs tracking-[0.24em] uppercase disabled:opacity-60 hover:bg-[color:var(--gold-strong)] transition-colors"
      >
        {loading ? "Sending…" : done ? "Received" : "Request Funds"}
        <ArrowRight size={14} />
      </button>

      <p
        data-testid="leadform-consent"
        className="mt-5 text-xs text-[color:var(--bone)]/55 leading-relaxed max-w-xl"
      >
        By submitting, you agree to our{" "}
        <Link to="/terms" className="text-[color:var(--gold)] border-b border-[color:var(--gold)]/40 hover:border-[color:var(--gold)]">
          Terms
        </Link>{" "}
        and{" "}
        <Link to="/privacy" className="text-[color:var(--gold)] border-b border-[color:var(--gold)]/40 hover:border-[color:var(--gold)]">
          Privacy Policy
        </Link>
        . You authorize Steady Path Funding and our lender partners to contact you at the number and email provided.
      </p>
    </motion.form>
  );
};

export default LeadForm;
